(ns app.front.view.property.v2.map-section
  (:require [app.domain.location.model :as location-model]
            [app.domain.property.model :as property-model]
            [app.front.municipality :as municipality]
            [app.front.ui :as ui]
            [app.front.util :as futil]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.property.subs :as property-subs]
            [app.front.view.property.units-editor :as units-editor]
            [app.front.view.workspace.address :as ws-address]
            [app.front.view.workspace.map :as workspace-map]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]))

(declare detail-row)

(defn- map-view [map-props]
  [:div.w-full.border.border-gray-300.rounded
   {:style {:height "400px" :max-height "400px" :overflow "hidden" :position "relative"}}
   [workspace-map/workspace-map map-props]])

(defn- municipality-map [tr municipality full-map?]
  (let [layers     (<sub [::municipality/municipal-layers (:domain-group-id municipality) false])
        map-center (<sub [::property-subs/map-center nil municipality])]
    (when map-center
      [:div
       [:div.mb-4.p-4.bg-blue-50.border.border-blue-200.rounded-lg
        [:div.text-gray-800.font-medium (tr [:property/map-new-property-not-located])]]

       [map-view
        (merge {:center              map-center
                :layers              layers
                :extended?           false
                :primaryMapMarkers   []
                :secondaryMapMarkers []
                :propertyGeometries  {:primaryPropertyGeometry      nil
                                      :additionalPropertyGeometries nil
                                      :roadSegmentGeometries        nil}
                :interactionState    "DEFAULT"}
               (when full-map?
                 {:extended?       true
                  :hiddenTools     workspace-map/default-hidden-tools
                  :hiddenDrawTools workspace-map/default-hidden-draw-tools}))]])))

(defn- address-search-box
  [{:keys [tr property-id]}]
  (let [property        (<sub [::property-subs/property property-id])
        municipality    (-> property :property/municipality)
        domain-group-id (-> property :property/group :group/id)]
    [:div.relative.flex-1.max-w-2xl
     [gis-search/address-search
      {:tr                tr
       :domain-group-id   domain-group-id
       :municipalities    [(:municipality/number municipality)]
       :show-existing?    false
       :class             "w-full"
       :light             false
       :on-select-address (fn [result]
                            (>evt [::property-subs/select-address-result result]))
       :clear-fn          (fn []
                            (>evt [::property-subs/reset-map-center])
                            (>evt [::property-subs/clear-address-search-result])
                            (>evt [::property-subs/set-search-started false])
                            (>evt [::property-subs/clear-geocoded-reference]))
       :on-error-evt      [::property-subs/search-error]}]]))

(defn- primary-markers [property-center selected-search-coords point-data]
  (cond
    (and point-data (= (:type point-data) "Point")) [(:coordinates point-data)]
    selected-search-coords [selected-search-coords]
    property-center [property-center]
    :else []))

(defn- secondary-markers [drawing-mode point-data]
  (cond
    (= drawing-mode :polygon) []
    (and point-data (= (:type point-data) "Point")) [(:coordinates point-data)]
    :else []))

(defn- reference-markers [geocoded-reference-point]
  (if geocoded-reference-point
    [geocoded-reference-point]
    []))

(defn- property-geometries [property polygon-data]
  (let [location        (-> property :entity/location)
        property-center (location-model/property-location->center-coords property)]
    (cond-> {:primaryPropertyGeometry      (when property-center
                                             (property-model/geojson-feature
                                               (-> location :location/shape)))
             :additionalPropertyGeometries nil
             :roadSegmentGeometries        nil}
      (and polygon-data (not property-center))
      (assoc :primaryPropertyGeometry (property-model/geojson-feature polygon-data)))))

(defn- handle-map-click [drawing-enabled? drawing-mode on-drawing-complete event]
  (when drawing-enabled?
    (let [coordinate (js->clj (futil/oget event :coordinate) :keywordize-keys true)
          [x y]      coordinate]
      (case drawing-mode
        :point (when on-drawing-complete
                 (on-drawing-complete {:type        "Point"
                                       :coordinates [x y]}))
        nil))))

(defn- drawing-instruction-overlay [drawing-enabled? drawing-mode tr]
  (when drawing-enabled?
    [:div.absolute.px-4.py-3.rounded-lg.shadow-lg.z-10.text-sm.text-center.border
     {:class "bg-white/95"
      :style {:top "20px" :left "50%" :transform "translateX(-50%)"}}
     (case drawing-mode
       :point (tr [:property/map-click-to-place-point])
       :polygon (tr [:property/map-use-area-tool-to-draw])
       "")]))

(defn- reference-marker-overlay [geocoded-reference-point tr]
  (when geocoded-reference-point
    [:div.absolute.px-4.py-3.rounded-lg.shadow-lg.z-10.text-sm.text-center.border
     {:class "bg-white/95"
      :style {:top "20px" :left "50%" :transform "translateX(-50%)"}}
     (tr [:property/map-geocoded-reference-instruction])]))

(defn- parse-coordinate [value]
  (when-not (str/blank? value)
    (let [n (js/Number value)]
      (when-not (js/isNaN n)
        n))))

(defn- coordinate-error [x y]
  (cond
    (or (str/blank? x)
        (str/blank? y))
    :property/coordinate-required-error

    (or (nil? (parse-coordinate x))
        (nil? (parse-coordinate y)))
    :property/coordinate-format-error))

(defn- point-on-segment? [[x y] [[x1 y1] [x2 y2]]]
  (and (<= (min x1 x2) x (max x1 x2))
       (<= (min y1 y2) y (max y1 y2))
       (< (js/Math.abs (- (* (- x x1) (- y2 y1))
                          (* (- y y1) (- x2 x1))))
          1e-9)))

(defn- point-in-ring? [[x y :as point] ring]
  (let [segments (partition 2 1 (conj (vec ring) (first ring)))]
    (or (some #(point-on-segment? point %) segments)
        (->> segments
             (filter (fn [[[_ y1] [_ y2]]]
                       (not= (> y1 y) (> y2 y))))
             (filter (fn [[[x1 y1] [x2 y2]]]
                       (< x (+ x1 (/ (* (- y y1) (- x2 x1))
                                     (- y2 y1))))))
             count
             odd?))))

(defn- point-in-polygon-coordinates? [point [outer-ring & holes]]
  (and (point-in-ring? point outer-ring)
       (not-any? #(point-in-ring? point %) holes)))

(defn- point-in-geometry? [point {:keys [type coordinates]}]
  (case type
    "Polygon" (point-in-polygon-coordinates? point coordinates)
    "MultiPolygon" (some #(point-in-polygon-coordinates? point %) coordinates)
    true))

(defn- should-confirm-polygon-removal? [property coordinates]
  (when-let [shape (-> property :entity/location :location/shape)]
    (not (point-in-geometry? coordinates shape))))

(defn- save-point [property coordinates]
  (let [property-id (:db/id property)]
    (if (should-confirm-polygon-removal? property coordinates)
      (>evt [::property-subs/show-remove-polygon-confirm property-id coordinates])
      (do
        (>evt [::property-subs/set-saving-location true])
        (>evt [::property-subs/save-point-to-backend property-id coordinates])))))

(defn- remove-polygon-confirm-modal [tr]
  (when-let [{:keys [property-id coordinates]} (<sub [::property-subs/remove-polygon-confirm])]
    [ui/confirm-modal
     {:tr              tr
      :id              "property-remove-polygon-confirm-modal"
      :modal-title     (tr [:property/remove-polygon-confirm-title])
      :open?           true
      :yes-button-text (tr [:property/remove-polygon-confirm-yes])
      :on-close        #(>evt [::property-subs/show-remove-polygon-confirm nil nil])
      :on-yes-click    #(do
                          (>evt [::property-subs/set-saving-location true])
                          (>evt [::property-subs/show-remove-polygon-confirm nil nil])
                          (>evt [::property-subs/save-point-to-backend property-id coordinates true]))}
     (tr [:property/remove-polygon-confirm-text])]))

(def coordinate-input-errors
  #{:property/coordinate-required-error
    :property/coordinate-format-error})

(defn- coordinate-input
  [{:keys [tr property-id coordinate value error]}]
  (let [invalid? (contains? coordinate-input-errors error)]
    [ui/text-input {:id           (str "property-coordinate-" (name coordinate) "-" property-id)
                    :label-text   (tr [(keyword "property" (str "coordinate-" (name coordinate)))])
                    :value        (or value "")
                    :invalid      invalid?
                    :invalid-text (when invalid?
                                    (tr [error]))
                    :on-change    #(>evt [::property-subs/set-coordinate-edit
                                          property-id coordinate (futil/tval %)])}]))

(defn- coordinates-panel
  [{:keys [tr property editable? point-data saving?]}]
  (let [property-id     (:db/id property)
        selected-center (when (= (:type point-data) "Point")
                          (:coordinates point-data))
        display-center  (or selected-center
                            (location-model/property-location->center-coords property))
        coordinates     (futil/format-coordinate-pair display-center)
        {:keys [x y]}   (<sub [::property-subs/coordinate-edit-values property-id coordinates])
        dirty?          (<sub [::property-subs/coordinate-edit-dirty? property-id coordinates])
        error           (when editable?
                          (coordinate-error x y))]
    [:div.mb-4
     [:div.text-sm.font-medium.text-black.mb-2 (tr [:property/coordinates])]
     (if editable?
       [:div
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-3
         [coordinate-input {:tr          tr
                            :property-id property-id
                            :coordinate  :x
                            :value       x
                            :error       error}]
         [coordinate-input {:tr          tr
                            :property-id property-id
                            :coordinate  :y
                            :value       y
                            :error       error}]]
        [:div.flex.justify-end.gap-2.mt-3
         [ui/button {:kind     :secondary
                     :size     :small
                     :disabled (boolean (or (not dirty?) saving?))
                     :on-click #(>evt [::property-subs/clear-coordinate-edit property-id])}
          (tr [:cancel])]
         [ui/button {:kind     :primary
                     :size     :small
                     :disabled (boolean (or error (not dirty?) saving?))
                     :on-click #(save-point property
                                            [(parse-coordinate x)
                                             (parse-coordinate y)])}
          (if saving? (tr [:property/map-saving]) (tr [:save]))]]]
       (when display-center
         [:div.grid.grid-cols-1.md:grid-cols-2.gap-3
          [detail-row {:label (tr [:property/coordinate-x])
                       :value (:x coordinates)}]
          [detail-row {:label (tr [:property/coordinate-y])
                       :value (:y coordinates)}]]))]))

(defn- save-cancel-overlay [show-save-buttons? saving? on-save-location on-cancel-location tr]
  (when show-save-buttons?
    [:div.absolute.px-4.py-3.rounded-lg.shadow-lg.z-10.flex.items-center.gap-2.border
     {:class "bg-white/95"
      :style {:bottom "20px" :left "50%" :transform "translateX(-50%)"}}
     [ui/button {:kind     :primary
                 :size     :small
                 :disabled saving?
                 :on-click on-save-location}
      (if saving? (tr [:property/map-saving]) (tr [:property/map-save-location]))]
     [ui/button {:kind     :tertiary
                 :size     :small
                 :disabled saving?
                 :on-click on-cancel-location}
      (tr [:property/map-cancel])]]))

(def hidden-tools-for-polygon
  [workspace-map/default-tool
   workspace-map/layer-tool
   workspace-map/measure-tool])

(def hidden-tools-for-point
  (conj hidden-tools-for-polygon
        workspace-map/draw-tool))

(def hidden-draw-tools-for-polygon
  [workspace-map/point-draw-tool
   workspace-map/line-string-draw-tool
   workspace-map/circle-draw-tool
   workspace-map/modify-draw-tool])

(defn- property-map
  [{:keys [tr property municipality drawing-mode polygon-data point-data
           on-drawing-complete on-save-location on-cancel-location saving? full-map?]}]
  (let [municipality-id          (if property
                                   (-> property :property/group :group/id)
                                   (:domain-group-id municipality))
        layers                   (<sub [::municipality/municipal-layers municipality-id false])
        drawing-enabled?         (not= drawing-mode :none)
        show-save-buttons?       (or (and point-data
                                          (or (= drawing-mode :point)
                                              (= drawing-mode :none))
                                          (not saving?))
                                     (and polygon-data
                                          (= drawing-mode :polygon)
                                          (not saving?)))
        property-center          (location-model/property-location->center-coords property)
        selected-search-coords   (<sub [::property-subs/selected-search-coordinates])
        geocoded-reference-point (<sub [::property-subs/geocoded-reference-point])
        custom-center            (<sub [::property-subs/custom-map-center])
        map-center               (or custom-center
                                     property-center
                                     (<sub [::property-subs/map-center property municipality]))]
    (when map-center
      [:div
       (when (not property-center)
         [:div.mb-4.p-4.bg-blue-50.border.border-blue-200.rounded-lg
          [:div.text-gray-800.font-medium (tr [:property/map-new-property-not-located])]])
       [:div.relative
        [map-view
         {:center              map-center
          :layers              layers
          :extended?           (or full-map? drawing-enabled?)
          :primaryMapMarkers   (primary-markers property-center selected-search-coords point-data)
          :secondaryMapMarkers (cond
                                 (= drawing-mode :polygon) []
                                 point-data                (secondary-markers drawing-mode point-data)
                                 geocoded-reference-point  (reference-markers geocoded-reference-point)
                                 :else                     [])
          :propertyGeometries  (if (or (= drawing-mode :polygon)
                                       (and polygon-data saving?))
                                 {:primaryPropertyGeometry      (when polygon-data
                                                                  (property-model/geojson-feature polygon-data))
                                  :additionalPropertyGeometries nil
                                  :roadSegmentGeometries        nil}
                                 (property-geometries property polygon-data))
          :interactionState    (if (= drawing-mode :polygon)
                                 "DRAW" "DEFAULT")
          :drawTool            (when (= drawing-mode :polygon) "Polygon")
          :hiddenTools         (cond
                                 (= drawing-mode :polygon) hidden-tools-for-polygon
                                 (= drawing-mode :point)   hidden-tools-for-point
                                 full-map?                 workspace-map/default-hidden-tools
                                 :else                     [])
          :hiddenDrawTools     (cond
                                 (= drawing-mode :polygon) hidden-draw-tools-for-polygon
                                 (= drawing-mode :point)   workspace-map/default-hidden-draw-tools
                                 full-map?                 workspace-map/default-hidden-draw-tools
                                 :else                     [])
          :onDrawingComplete   (when (= drawing-mode :polygon)
                                 (fn [drawn-data]
                                   (let [data (js->clj drawn-data :keywordize-keys true)]
                                     (when (= (:type data) "Polygon")
                                       (>evt [::property-subs/save-polygon-data (-> property :db/id) (:geometry data)])))))
          :events              (if (and drawing-enabled? (not= drawing-mode :polygon))
                                 [{:eventType     "singleclick"
                                   :eventFunction (partial handle-map-click drawing-enabled? drawing-mode on-drawing-complete)}]
                                 [])}]
        [drawing-instruction-overlay drawing-enabled? drawing-mode tr]
        [reference-marker-overlay geocoded-reference-point tr]
        [save-cancel-overlay show-save-buttons? saving? on-save-location on-cancel-location tr]]])))

(defn- handle-drawing-complete [property-id drawn-feature]
  (case (:type drawn-feature)
    "Point"
    (do
      (>evt [::property-subs/save-point-data property-id drawn-feature])
      (>evt [::property-subs/set-drawing-mode :none]))
    "Polygon"
    (do
      (>evt [::property-subs/save-polygon-data property-id drawn-feature])
      (>evt [::property-subs/set-drawing-mode :none]))))

(defn- handle-save-location [property point-data polygon-data]
  (cond
    point-data
    (let [coords (:coordinates point-data)]
      (save-point property coords))

    polygon-data
    (do
      (>evt [::property-subs/set-saving-location true])
      (>evt [::property-subs/save-polygon-to-backend (:db/id property) polygon-data])
      (>evt [::property-subs/set-drawing-mode :none]))

    :else
    (>evt [::property-subs/set-saving-location false])))

(defn- handle-cancel-location [property-id]
  (>evt [::property-subs/set-drawing-mode :none])
  (>evt [::property-subs/clear-point-data property-id])
  (>evt [::property-subs/clear-drawing-data property-id])
  (>evt [::property-subs/clear-geocoded-reference]))

(defn- editing-instructions
  [tr]
  [:div.mb-4.text-sm.text-black
   [:div.mb-1
    (tr [:property/map-point-tool-instruction-before])
    [:span.font-bold (tr [:property/map-point-tool-instruction-bold])]
    (tr [:property/map-point-tool-instruction-after])]
   [:div.mb-3
    (tr [:property/map-polygon-tool-instruction-before])
    [:span.font-bold (tr [:property/map-polygon-tool-instruction-bold])]
    (tr [:property/map-polygon-tool-instruction-after])]
   [:div.mb-4.text-sm.text-black
    (tr [:property/map-view-modification-note])]])

(defn- drawing-tool-buttons [tr drawing-mode property-id]
  (let [search-value   (<sub [::property-subs/address-search-value])
        can-geolocate? (<sub [::property-subs/can-geolocate?])]

    [:div.flex.items-center.gap-2
     (when can-geolocate?
       [ui/button {:kind          :secondary
                   :size          :small
                   :icon          i/location
                   :icon-position :right
                   :on-click      #(>evt [::property-subs/geolocate-address property-id search-value])}
        (tr [:property/map-geolocate-address])])

     [ui/button (cond-> {:kind          (if (= drawing-mode :point) :primary :secondary)
                         :size          :small
                         :icon          i/add-point
                         :icon-position :right
                         :on-click      #(do
                                           (when (not= drawing-mode :point)
                                             (>evt [::property-subs/clear-drawing-data property-id])
                                             (>evt [::property-subs/clear-point-data property-id]))
                                           (>evt [::property-subs/set-drawing-mode (if (= drawing-mode :point) :none :point)]))}
                  (= drawing-mode :point) (assoc :class-name "bg-blue-600 text-white border-blue-600"))
      (tr [:property/map-point-tool])]

     [ui/button (cond-> {:kind          (if (= drawing-mode :polygon) :primary :secondary)
                         :size          :small
                         :icon          i/add-area
                         :icon-position :right
                         :on-click      #(do
                                           (if (= drawing-mode :polygon)
                                             (>evt [::property-subs/set-drawing-mode :none])
                                             (do
                                               (>evt [::property-subs/clear-drawing-data property-id])
                                               (>evt [::property-subs/set-drawing-mode :polygon]))))}
                  (= drawing-mode :polygon) (assoc :class-name "bg-blue-600 text-white border-blue-600"))
      (tr [:property/map-polygon-tool])]]))

(defn- edit-detail-section
  [{:keys [id label value on-change on-blur]}]
  [:div.mb-4
   [:label.text-sm.font-medium.text-black.mb-1.block {:for (str "edit-details-" id)} label]
   [ui/text-input {:id         (str "edit-details-" id)
                   :label-text label
                   :hide-label true
                   :value      (or value "")
                   :on-change  #(on-change (futil/tval %))
                   :on-blur    (when on-blur #(on-blur (futil/tval %)))}]])

(defn- detail-row [{:keys [label value]}]
  [:div.flex.flex-row.w-full.justify-between.mb-3
   [:div
    [:div.text-sm.text-black label]
    [:strong.pt-2.block (or value "—")]]])

(defn- details-panel [{:keys [tr property editable?]}]
  (let [property-id   (:db/id property)
        dirty?        (<sub [::property-subs/edit-details-dirty?])
        address-edit? (contains? (<sub [::property-subs/edit-details]) :address)
        saving?       (<sub [:comm/command-processing? :property/edit-details])
        pin           (<sub [::property-subs/edit-detail-value :property/id])
        legal         (<sub [::property-subs/edit-detail-value :property/legal])
        address       (<sub [::property-subs/edit-address])
        parse-address (<sub [::ws-address/parser
                             (keyword (-> property :property/municipality :municipality/subdivision))])]
    [:div.mb-4
     (if editable?
       [:<>
        [edit-detail-section {:id        "property-address"
                              :label     (tr [:workspace/address])
                              :value     (:address/full-address address)
                              :on-change #(rf/dispatch-sync [::property-subs/set-edit-address
                                                             (assoc address :address/full-address %)])
                              :on-blur   (when address-edit?
                                           #(>evt [::ws-address/set-and-parse-final-address
                                                   ::property-subs/set-edit-address
                                                   parse-address
                                                   address
                                                   %]))}]
        [edit-detail-section {:id        "property-id"
                              :label     (tr [:property/id])
                              :value     pin
                              :on-change #(>evt [::property-subs/set-edit-detail :property/id %])}]
        [edit-detail-section {:id        "property-legal"
                              :label     (tr [:property/legal])
                              :value     legal
                              :on-change #(>evt [::property-subs/set-edit-detail :property/legal %])}]
        [:div.flex.justify-end.gap-2.mb-4
         [ui/button {:kind     :secondary
                     :size     :small
                     :disabled (boolean (or (not dirty?) saving?))
                     :on-click #(>evt [::property-subs/clear-edit-details])}
          (tr [:cancel])]
         [ui/button {:kind     :primary
                     :size     :small
                     :disabled (boolean (or (not dirty?) saving?))
                     :on-click #(>evt [::property-subs/save-edit-details property-id])}
          (if saving? (tr [:saving]) (tr [:save]))]]]
       [:<>
        [detail-row {:label (tr [:workspace/address])
                     :value (property-model/full-address property)}]
        [detail-row {:label (tr [:property/id])
                     :value pin}]
        [detail-row {:label (tr [:property/legal])
                     :value legal}]])]))

(defn- editing-tools-bar [tr property-id drawing-mode]
  [:div.mb-4
   [:div.mb-2.text-sm.font-medium.text-black (tr [:property/property-search-label])]
   [:div.flex.items-center.gap-4
    [address-search-box {:tr tr :property-id property-id}]
    [drawing-tool-buttons tr drawing-mode property-id]]])

(defn- editing-controls [tr property-id drawing-mode]
  [:div.mb-4
   [editing-instructions tr]
   [:div.mb-2.text-sm.font-medium.text-black (tr [:property/search-address-label])]
   [:div.flex.items-center.gap-4
    [address-search-box {:tr tr :property-id property-id}]
    [drawing-tool-buttons tr drawing-mode property-id]]])

(defn map-section [{:keys [tr property]}]
  (let [property-id         (-> property :db/id)
        editing?            (<sub [::property-subs/editing?])
        can-edit?           (-> property :granted-permissions :permission.property/write)
        property-db-units?  (<sub [::ff/enabled? :feature-flag.id/property-db-units])
        drawing-mode        (<sub [::property-subs/drawing-mode])
        polygon-data        (<sub [::property-subs/saved-polygon-data property-id])
        point-data          (<sub [::property-subs/saved-point-data property-id])
        saving?             (<sub [::property-subs/saving-location?])
        coordinate-editing? (<sub [::ff/enabled? :feature-flag.id/property-data-integrity-tooling])
        location            (-> property :entity/location)
        municipality        (-> property :property/municipality)
        on-drawing-complete (partial handle-drawing-complete property-id)
        on-save-location    (partial handle-save-location property point-data polygon-data)
        on-cancel-location  (partial handle-cancel-location property-id)
        edit-mode?          (and editing? can-edit?)
        two-column?         property-db-units?
        map-el              (if (or edit-mode? location)
                              ^{:key (str "property-map-" property-id)}
                              [property-map {:tr                  tr
                                             :property            property
                                             :municipality        municipality
                                             :drawing-mode        drawing-mode
                                             :polygon-data        polygon-data
                                             :point-data          point-data
                                             :full-map?           property-db-units?
                                             :on-drawing-complete on-drawing-complete
                                             :on-save-location    on-save-location
                                             :on-cancel-location  on-cancel-location
                                             :saving?             saving?}]
                              [municipality-map tr municipality property-db-units?])]
    [:div
     (if two-column?
       [:div.grid.grid-cols-1.lg:grid-cols-2.gap-4
        [:div
         map-el
         (when edit-mode?
           [:div.mt-4
            [editing-tools-bar tr property-id drawing-mode]])]
        [:div
         [units-editor/read-only-info-columns {:tr tr :property property}]
         [details-panel {:tr tr :property property :editable? edit-mode?}]
         (when (and coordinate-editing?
                    (or edit-mode? (location-model/property-location->center-coords property) point-data))
           [coordinates-panel {:tr         tr
                               :property   property
                               :editable?  edit-mode?
                               :point-data point-data
                               :saving?    saving?}])]]
       [:div
        (when edit-mode?
          [editing-controls tr property-id drawing-mode])
        map-el])
     (when property-db-units?
       [units-editor/editor-list {:tr          tr
                                  :editable?   edit-mode?
                                  :properties  [property]
                                  :units       (<sub [::property-subs/property-units-for-editor])
                                  :designators (<sub [::property-subs/property-unit-designators])}])
     [remove-polygon-confirm-modal tr]]))
